import { useT } from '@/core/I18nProvider';

import { Frame } from './Frame';
import { useSteppedLoop } from './use-stepped-loop';

import type { ReactElement } from 'react';

/**
 * "It does the work" — a question, a card showing the exact change, an
 * approval, and the receipt.
 *
 * ## Why the card is the centre of the frame
 *
 * The page's hardest claim is that an agent can be trusted to act, and the
 * whole of the answer is this card: the visitor sees the before and the after
 * before anything happens. So the sequence deliberately spends two of its five
 * beats on the card sitting there UNAPPROVED — the pause is the product.
 *
 * The rows are the ADR-0170 change preview reduced to its shape: a struck-out
 * old value, a new one, and nothing else. A summary would be the thing this
 * design exists to replace.
 */
const BEATS = { ASK: 0, THINK: 1, CARD: 2, APPROVED: 3, DONE: 4 } as const;
const STEPS = 5;

export function ActionVisual(): ReactElement {
  const { t } = useT();
  const { ref, step } = useSteppedLoop<HTMLDivElement>(STEPS, 1500);

  return (
    <div ref={ref} aria-hidden="true" data-features-visual>
      <Frame className="w-full">
        <div className="flex min-h-[19rem] flex-col gap-3 p-5">
          {/* The visitor's question. */}
          <div className="flex justify-end">
            <span className="max-w-[80%] rounded-[13px] rounded-ee-[4px] bg-[color:var(--bd-ink)] px-3.5 py-2 text-[0.83rem] leading-[1.5] text-white">
              {t('features.showcase.action.ask')}
            </span>
          </div>

          {/* Working. Three dots, then the reply — the dots exist so the card
              does not appear to have been waiting all along. */}
          {step === BEATS.THINK ? (
            <span className="flex w-fit gap-1 rounded-full bg-[color:var(--bd-sunk)] px-3 py-2">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="size-[5px] rounded-full bg-[color:var(--bd-subtle)] sx-dot"
                  style={{ animationDelay: `${i * 0.16}s` }}
                />
              ))}
            </span>
          ) : null}

          {step >= BEATS.CARD ? (
            <>
              <span className="max-w-[86%] text-[0.83rem] leading-[1.55] text-[color:var(--bd-body)]">
                {t('features.showcase.action.reply')}
              </span>

              {/* The change preview. */}
              <div
                className="rounded-[11px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-page)] p-3.5 transition-all duration-500"
                style={{
                  boxShadow:
                    step === BEATS.CARD ? '0 0 0 3px rgb(var(--bd-blue-rgb) / 0.10)' : 'none',
                }}
              >
                <p className="font-mono rtl:font-sans text-[9.5px] uppercase tracking-[0.14em] text-[color:var(--bd-subtle)] rtl:normal-case rtl:tracking-normal">
                  {t('features.showcase.action.cardLabel')}
                </p>
                <dl className="mt-2.5 flex flex-col gap-1.5">
                  {['plan', 'seats'].map((row) => (
                    <div key={row} className="flex items-baseline gap-2 text-[0.8rem]">
                      <dt className="w-[38%] flex-none truncate text-[color:var(--bd-muted)]">
                        {t(`features.showcase.action.row.${row}.label`)}
                      </dt>
                      <dd className="flex min-w-0 items-baseline gap-2">
                        <span className="truncate text-[color:var(--bd-subtle)] line-through">
                          {t(`features.showcase.action.row.${row}.before`)}
                        </span>
                        <span aria-hidden="true" className="text-[color:var(--bd-subtle)]">
                          →
                        </span>
                        <span className="truncate font-medium text-[color:var(--bd-ink)]">
                          {t(`features.showcase.action.row.${row}.after`)}
                        </span>
                      </dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-3.5 flex items-center gap-2">
                  {/* Brand BLUE, not the gradient. The gradient sweeps cyan →
                      blue → violet, and white on its cyan end measures 1.8:1 —
                      the `ui-design.md` rule that the gradient never carries
                      text, landing on the one mock that had a filled button in
                      it. Flat #2b62ff takes white at 4.89:1. The subtree is
                      `aria-hidden`, so axe never looked; `pnpm
                      a11y:inert-contrast` is what measured it, and it now
                      audits this page for that reason. */}
                  <span
                    className="rounded-[7px] px-3 py-1.5 text-[0.76rem] font-medium text-white transition-transform duration-300"
                    style={{
                      background: 'var(--bd-blue)',
                      transform: step === BEATS.APPROVED ? 'scale(0.94)' : 'none',
                    }}
                  >
                    {t('features.showcase.action.approve')}
                  </span>
                  <span className="rounded-[7px] border border-[color:var(--bd-line-2)] px-3 py-1.5 text-[0.76rem] text-[color:var(--bd-muted)]">
                    {t('features.showcase.action.cancel')}
                  </span>
                </div>
              </div>
            </>
          ) : null}

          {/* The receipt. Two ticks and the word "logged" — the audit trail is
              not a separate feature the reader has to go and find, it is the
              last frame of every action. */}
          <div
            className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1.5 transition-opacity duration-500"
            style={{ opacity: step === BEATS.DONE ? 1 : 0 }}
          >
            {['a', 'b'].map((id) => (
              <span
                key={id}
                className="flex items-center gap-1.5 text-[0.79rem] text-[color:var(--bd-body)]"
              >
                <svg viewBox="0 0 12 12" className="size-[11px] flex-none" fill="none">
                  <path
                    d="M2.5 6.2 4.8 8.5 9.5 3.8"
                    stroke="var(--bd-ok-ink)"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {t(`features.showcase.action.done.${id}`)}
              </span>
            ))}
            <span className="font-mono rtl:font-sans text-[0.72rem] tracking-[0.08em] text-[color:var(--bd-subtle)] rtl:tracking-normal">
              {t('features.showcase.action.logged')}
            </span>
          </div>
        </div>
      </Frame>
    </div>
  );
}
